/*
 * Critter rendering. One CritterView per critter (player or NPC); .update(t)
 * each frame moves the container, applies a soft "bob" idle, and refreshes
 * the bubble + label.
 *
 * The shape is intentionally close to the v0.2 drawing math so the silhouette
 * stays recognisable — we don't aim for pixel parity.
 */

import { Container, Graphics, Text } from 'pixi.js';
import type { AnyCritter } from '@/state/types';
import { furAt } from '@/content/furColors';
import { hatNameAt } from '@/content/hats';
import { buildHat, isAnimatedHat } from '@/render/hat';
import { createEmoteView, emoteBodyOffset, type EmoteView } from '@/render/emote';
import type { EmoteName } from '../../../shared/protocol';

export interface CritterView {
  /** Root container (positioned in world coords). */
  container: Container;
  /** Update once per frame with the current critter state and time. */
  update(c: AnyCritter, t: number): void;
}

export function createCritterView(): CritterView {
  const root = new Container();

  // Layered children, bottom → top:
  const shadow = new Graphics();
  const body = new Graphics();
  const emote: EmoteView = createEmoteView();
  const hat = new Graphics();
  const label = new Text({
    text: '',
    style: {
      fontFamily: 'Quicksand, sans-serif',
      fontSize: 10,
      fontWeight: '600',
      fill: 0xf4ecd8,
      align: 'center',
      dropShadow: { color: 0x000000, alpha: 0.5, distance: 0, blur: 2, angle: 0 },
    },
  });
  label.anchor.set(0.5, 1);

  const bubble = new Container();
  bubble.visible = false;
  const bubbleBg = new Graphics();
  const bubbleText = new Text({
    text: '',
    style: {
      fontFamily: 'Quicksand, sans-serif',
      fontSize: 12,
      fontWeight: '600',
      fill: 0x2d4a3a,
    },
  });
  bubble.addChild(bubbleBg);
  bubble.addChild(bubbleText);

  root.addChild(shadow);
  root.addChild(body);
  root.addChild(emote.container);
  root.addChild(hat);
  root.addChild(label);
  root.addChild(bubble);

  // Cached "what's currently drawn" so we don't rebuild static geometry every frame.
  let lastColor = -1;
  let lastHatIdx = -1;
  let lastBubble: string | null = null;

  function rebuildBody(colorIdx: number): void {
    const colors = furAt(colorIdx);
    const bodyColor = parseInt(colors.body.slice(1), 16);
    const bellyColor = parseInt(colors.belly.slice(1), 16);

    body.clear();

    // Ears (back layer).
    body.ellipse(-10, -13, 5, 7).fill(bodyColor);
    body.ellipse(10, -13, 5, 7).fill(bodyColor);
    // Ear inner.
    body.ellipse(-10, -13, 2.5, 4).fill(bellyColor);
    body.ellipse(10, -13, 2.5, 4).fill(bellyColor);

    // Head/body blob.
    body.ellipse(0, 0, 17, 19).fill(bodyColor);
    // Belly highlight.
    body.ellipse(0, 5, 9, 11).fill(bellyColor);

    // Cheek tint.
    body.circle(-9, 0, 2.5).fill({ color: 0xc9514c, alpha: 0.35 });
    body.circle(9, 0, 2.5).fill({ color: 0xc9514c, alpha: 0.35 });

    // Eyes — dark body + white catchlight.
    body.ellipse(-5, -4, 2, 2.5).fill(0x1a1410);
    body.ellipse(5, -4, 2, 2.5).fill(0x1a1410);
    body.circle(-4, -5, 0.9).fill(0xffffff);
    body.circle(6, -5, 0.9).fill(0xffffff);

    // Snout.
    body.ellipse(0, 1, 1.6, 1.1).fill(0x3a2820);

    // Smile.
    body
      .moveTo(-2, 4)
      .quadraticCurveTo(0, 5.5, 2, 4)
      .stroke({ color: 0x3a2820, width: 0.8, cap: 'round' });
  }

  function rebuildBubble(text: string): void {
    bubbleText.text = text;
    const tw = bubbleText.width;
    const bw = tw + 18;
    const bh = 22;
    const bx = 14;
    const by = -32;

    bubbleBg.clear();
    bubbleBg
      .roundRect(bx, by - bh / 2, bw, bh, 11)
      .fill({ color: 0xf4ecd8, alpha: 0.96 })
      .stroke({ color: 0x2d4a3a, alpha: 0.2, width: 1 });

    bubbleBg
      .moveTo(bx + 4, by + bh / 2 - 1)
      .lineTo(bx - 4, by + bh / 2 + 6)
      .lineTo(bx + 10, by + bh / 2 - 1)
      .closePath()
      .fill({ color: 0xf4ecd8, alpha: 0.96 });

    bubbleText.x = bx + 9;
    bubbleText.y = by - 6;
  }

  shadow.ellipse(0, 18, 16, 5).fill({ color: 0x000000, alpha: 0.35 });

  function update(c: AnyCritter, t: number): void {
    // Rebuild body if fur color changed.
    if (c.color !== lastColor) {
      rebuildBody(c.color);
      lastColor = c.color;
    }

    const hatName = hatNameAt(c.hat);
    if (c.hat !== lastHatIdx || isAnimatedHat(hatName)) {
      buildHat(hatName, hat, t);
      hat.y = -17;
      lastHatIdx = c.hat;
    }

    // Idle bob — players bob a little stronger than NPCs and in phase with time,
    // NPCs bob offset by their x so they don't all sync.
    const isPlayer = 'isPlayer' in c && c.isPlayer === true;
    const bob = isPlayer
      ? Math.sin(t * 0.004) * 1.2
      : Math.sin(t * 0.003 + c.x * 0.01) * 1;

    // Emote drives a small body offset (dance sway, sit settle).
    const now = performance.now();
    const activeEmote: EmoteName | null =
      c.emote && c.emoteExpiresAt && c.emoteExpiresAt > now
        ? (c.emote as EmoteName)
        : null;
    emote.setEmote(activeEmote);
    emote.update(t);
    const emoteOffset = emoteBodyOffset(activeEmote, t);

    root.position.set(c.x + emoteOffset.x, c.y + bob + emoteOffset.y);

    // Idle = recently-disconnected remote critter. Grey-out placeholder until
    // the proper idle visual (egg / flower) lands. Local player and NPCs are
    // always 'active' so this never hits today; sits ready for TASK-06.
    root.alpha = c.state === 'idle' ? 0.45 : 1;

    // Label.
    if (label.text !== c.name) label.text = c.name;
    label.style.fill = isPlayer ? 0xffe9a8 : 0xf4ecd8;
    label.position.set(0, -32);

    // Bubble (reuses `now` from above).
    const hasBubble = c.bubble && c.bubbleExpiresAt > now;
    if (hasBubble && c.bubble !== lastBubble) {
      rebuildBubble(c.bubble!);
      lastBubble = c.bubble;
    }
    if (hasBubble) {
      // Fade out over the last 0.4s of the bubble's life. (v0.2 also faded in
      // by tying alpha to bubbleTime; in practice this reads as "softens away".)
      const remainingSec = (c.bubbleExpiresAt - now) / 1000;
      const a = Math.min(1, Math.max(0, remainingSec / 0.4));
      bubble.alpha = a;
      bubble.visible = true;
    } else {
      bubble.visible = false;
      lastBubble = null;
    }
  }

  return { container: root, update };
}
